import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useDispatch } from "react-redux";
import { getMyAllotments, downloadAllotmentLetter } from "../../../../core/data/redux/staffQuarterSlice";
import { toast } from "react-toastify";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";

const MyAllotments: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const [allotments, setAllotments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    dispatch(getMyAllotments({}))
      .then((res: any) => {
        if (res.payload?.success && Array.isArray(res.payload.data)) {
          setAllotments(res.payload.data);
        } else if (Array.isArray(res.payload)) {
          setAllotments(res.payload);
        } else if (Array.isArray(res.payload?.data)) {
          setAllotments(res.payload.data);
        } else if (Array.isArray(res.payload?.data?.data)) {
          setAllotments(res.payload.data.data);
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  const handleDownloadLetter = (registrationId: any) => {
    if (!registrationId) {
      toast.error("Registration ID not found.");
      return;
    }
    setDownloadingId(registrationId);
    dispatch(downloadAllotmentLetter({ registrationId }))
      .then((res: any) => {
        if (res.payload) {
          const blob = res.payload instanceof Blob
            ? res.payload
            : new Blob([res.payload], { type: 'application/pdf' });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `allotment-letter_${registrationId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success("Allotment letter downloaded successfully!");
        } else {
          toast.error("Failed to download allotment letter.");
        }
      })
      .catch(() => {
        toast.error("An error occurred while downloading the allotment letter.");
      })
      .finally(() => {
        setDownloadingId(null);
      });
  };

  const getStatusBadge = (s: string) => {
    const status = s?.toUpperCase();
    if (status === "ACTIVE" || status === "ALLOTTED") return "badge bg-success";
    if (status === "VACATED" || status === "REJECTED") return "badge bg-danger";
    return "badge bg-secondary";
  };

  const formatDate = (date: any) => {
    if (!date || date === "-" || date === "null" || date === "undefined" || date === "31/12/9999" || date === "9999-12-31") return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  interface ColumnDef {
    header: string;
    key?: string;
    className?: string;
    render?: (item: any) => React.ReactNode;
    format?: (val: any) => string;
  }

  const columns: ColumnDef[] = [
    { header: "Allotment ID", key: "referenceNumber", className: "fw-bold" },
    {
      header: "Flat No.",
      key: "flatNumber",
      render: (item: any) => formatFlatNoWithBlock(item)
    },
    { header: "Location", key: "location", render: (item: any) => item.location || item.locationName || item.flatDetails?.location || "-" },
    { header: "Quarter Type", key: "quarterType", render: (item: any) => item.quarterType || item.quarterTypeName || item.flatDetails?.quarterType || "-" },
    { header: "Category", key: "category", render: (item: any) => resolveFlatCategory(item) },
    { header: "From Date", key: "allotmentDate", render: (item: any) => formatDate(item.allotmentDate || item.created_at || item.createdAt) },
    {
      header: "To Date",
      key: "tentativeVacationDate",
      render: (item: any) => formatDate(item.tentativeVacationDate || item.vacatedDate || item.to_date || item.toDate || item.vacationDate)
    },
    {
      header: "Status",
      key: "status",
      render: (item: any) => (
        <span className={getStatusBadge(item.status)}>{item.status || "N/A"}</span>
      )
    },
    {
      header: "Action",
      key: "action",
      render: (item: any) => {
        const regId = item.registrationId || item.id;
        const isDownloading = downloadingId === regId;
        return (
          <button
            onClick={() => handleDownloadLetter(regId)}
            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
            disabled={isDownloading}
            style={{ padding: "0.25rem 0.5rem", fontSize: "12px" }}
          >
            {isDownloading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: "12px", height: "12px" }}></span>
                Downloading...
              </>
            ) : (
              <>
                <i className="ti ti-download me-1" style={{ fontSize: "12px" }}></i> Allotment Letter
              </>
            )}
          </button>
        );
      }
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">My Allotments</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard || "#"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  My Allotments
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-borderless table-hover">
                <thead className="table-light">
                  <tr>
                    {columns.map((col, idx) => (
                      <th key={idx}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span className="ms-2">Loading allotments...</span>
                      </td>
                    </tr>
                  ) : allotments.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-4 text-muted">
                        No allotments found.
                      </td>
                    </tr>
                  ) : (
                    allotments.map((item, idx) => (
                      <tr key={idx}>
                        {columns.map((col, cIdx) => (
                          <td key={cIdx} className={col.className || ""}>
                            {col.render ? col.render(item) : (col.format && col.key ? col.format(item[col.key]) : (col.key ? item[col.key] || "-" : "-"))}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAllotments;
